import { Trans, useLingui } from '@lingui/react/macro';
import { DiamondGrid, diamondGlows } from '../primitives';

/**
 * "B Corp desde 2015" — the certification band.
 *
 * Copy on the left, and on the right two stacked cards: a sand one tilted 3° behind an upright olive one
 * carrying the B Corp mark. Everything inside the card pair is a PERCENTAGE of the 348px frame the
 * hand-off gives it, so the whole assembly scales down on a narrow screen instead of being clipped —
 * the mark's padding is 28.2% of the card, which is the export's 84.058 of 297.75.
 */
export function BCorp() {
	const { t } = useLingui();
	return (
		<section className="bg-ink relative overflow-hidden text-white">
			{/* Two glows of DIFFERENT strength, both measured off the hand-off rather than taken from its
			    alpha values: a faint one in the top-right (peak ~0.26 white) and one twice as strong running
			    off the bottom-left corner (~0.51). The export gives the first an extra `opacity: 0.4` on top
			    of its gradient and the second none, which is the same 1:2 relationship. */}
			<DiamondGrid
				color="#FFFFFF"
				opacity={1}
				masks={[
					diamondGlows(
						{ x: 'calc(100% - 77px)', y: '76px', strength: 0.29 },
						{ x: '-35px', y: 'calc(100% + 6px)', strength: 0.83 },
					),
				]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col items-start gap-12 px-6 py-16 md:py-20 xl:h-[784px] xl:flex-row xl:items-center xl:gap-0 xl:px-0 xl:py-0">
				{/* 700 wide at left 127, and the block sits 15px above the band's centre in the export. */}
				<div className="flex w-full max-w-[700px] flex-col gap-4 xl:-mt-[15px] xl:ml-[127px]">
					<h2 className="text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`B Corp since 2015`}
					</h2>
					{/* #A8ACAB in the export, which is exactly white at 60% over this ink — so it is the token,
					    not a literal. The closing clause is the one part the design sets in full white. */}
					<p className="text-base leading-[1.5] text-white/60">
						<Trans>
							We were one of the first companies in Spain to be awarded the B Corp certificate. This international seal,
							granted by B Lab, recognises companies meeting the highest standards of social and environmental
							performance, transparency and corporate accountability, guaranteeing that{' '}
							<strong className="font-semibold text-white">we pursue the common good ahead of profit alone.</strong>
						</Trans>
					</p>
				</div>

				{/* The 348x438 frame the two cards are placed inside. Centred on mobile, at left 967 from `xl`. */}
				<div className="relative mx-auto aspect-[348/438] w-full max-w-[348px] xl:absolute xl:top-[173px] xl:left-[967px] xl:mx-0">
					{/* Behind, tilted, and offset down-right: 30.47 / 36.28 of the frame. */}
					<div
						aria-hidden
						className="bg-sand absolute top-[8.3%] left-[8.8%] h-[88.3%] w-[85.6%] rotate-3 rounded-[34px]"
					/>
					{/* In front and upright. The export lifts it 8px above the frame, hence the negative top. */}
					<div className="bg-olive absolute top-[-1.8%] left-0 flex h-[88.3%] w-[85.6%] items-center justify-center rounded-[34px] p-[28.2%]">
						<img src="/redesign/about/bcorp-certified.svg" alt={t`Certified B Corporation`} className="h-auto w-full" />
					</div>
				</div>
			</div>
		</section>
	);
}
